import { getRuntimeConfig } from '@shared/config/runtime';

// De minimis støtte threshold
// EU/EØS grense: 300 000 EUR over rullerende 3-årsperiode
// NOK-ekvivalent ved kurs ~11,5
export const DE_MINIMIS_THRESHOLD_NOK = 3_450_000;
// Fast beløp for demo-søknad (brukes i de minimis-test)
export const DE_MINIMIS_REQUESTED_AMOUNT_NOK = 500_000;

// Approximate EUR to NOK exchange rate for de minimis calculations
// Note: This is a simplified approximation for demo purposes.
// In production, consider fetching current exchange rates from Norges Bank API.
const EUR_TO_NOK_RATE = 11.5;

// Types for API response from Brønnøysundregistrene
export interface StotteTildeling {
  id?: number;
  stoettetiltaksnummer?: string;
  tildeltBeloep: {
    beloep: number;
    valuta: string;
  };
  tildelingsdato: string;
  eoesRettsligGrunnlag?: {
    statsstoettekategori?: string;
  };
  stoetteordning?: {
    navn: string;
    type: string;
  };
  stoetteGiver?: {
    navn: string;
  };
  rolle: Array<{
    type: string;
    rolleinnehaverVirksomhet?: {
      organisasjonsnummer: string;
      navn: string;
    };
  }>;
}

export interface SimplifiedTildeling {
  beloep: number;
  valuta: string;
  dato: string;
  ordning: string;
  giver: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseTildelinger(data: unknown): StotteTildeling[] {
  if (!isRecord(data) || !Array.isArray(data.stoettetildeling)) {
    throw new Error('Ugyldig svar fra støtteregisteret');
  }
  for (const item of data.stoettetildeling) {
    if (
      !isRecord(item) || !isRecord(item.tildeltBeloep) ||
      typeof item.tildeltBeloep.beloep !== 'number' ||
      !Number.isFinite(item.tildeltBeloep.beloep) || item.tildeltBeloep.beloep < 0 ||
      !['NOK', 'EUR'].includes(String(item.tildeltBeloep.valuta)) ||
      typeof item.tildelingsdato !== 'string' || !Number.isFinite(Date.parse(item.tildelingsdato))
    ) {
      throw new Error('Ugyldig tildeling fra støtteregisteret');
    }
  }
  return data.stoettetildeling as StotteTildeling[];
}

// Only an explicit, valid register response can establish support history.
export async function fetchPreviousSupport(
  orgnr: string,
  signal?: AbortSignal
): Promise<StotteTildeling[]> {
  if (!/^\d{9}$/.test(orgnr)) throw new Error('Organisasjonsnummer må være 9 siffer');
  const deadline = AbortSignal.timeout(15_000);
  const requestSignal = signal ? AbortSignal.any([signal, deadline]) : deadline;
  const response = await fetch(getRuntimeConfig().network.stotteregisterSearchPath, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    signal: requestSignal,
    body: JSON.stringify([
      { field: 'STOETTEMOTTAKER_ORGNR', value: orgnr, matchType: 'EXACT_MATCH' },
    ]),
  });
  if (!response.ok) throw new Error(`API error: ${response.status} ${response.statusText}`);
  return parseTildelinger(await response.json());
}

function isDeMinimisTildeling(tildeling: StotteTildeling): boolean {
  return (
    tildeling.eoesRettsligGrunnlag?.statsstoettekategori ===
      'statsstoettekategori.bagatellmessigStoette' ||
    tildeling.stoetteordning?.type === 'DE_MINIMIS'
  );
}

// Sum allocations from the last 3 fiscal years (de minimis period)
// Note: EU de minimis rules technically use "current fiscal year and two preceding fiscal years",
// but for Norwegian companies the fiscal year typically aligns with the calendar year.
// This implementation uses a rolling 3-year window as a simplified approximation.
export function sumTildelingerLastThreeYears(tildelinger: StotteTildeling[]): number {
  const threeYearsAgo = new Date();
  threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);

  return tildelinger
    .filter((t) => {
      const tildelingDate = new Date(t.tildelingsdato);
      return isDeMinimisTildeling(t) && tildelingDate >= threeYearsAgo;
    })
    .reduce((sum, t) => {
      // Convert EUR to NOK if needed
      const beloepNOK =
        t.tildeltBeloep.valuta === 'EUR'
          ? t.tildeltBeloep.beloep * EUR_TO_NOK_RATE
          : t.tildeltBeloep.beloep;
      return sum + beloepNOK;
    }, 0);
}

// Simplify tildelinger for display
export function simplifyTildelinger(tildelinger: StotteTildeling[]): SimplifiedTildeling[] {
  const threeYearsAgo = new Date();
  threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);

  return tildelinger
    .filter((t) =>
      isDeMinimisTildeling(t) && new Date(t.tildelingsdato) >= threeYearsAgo
    )
    .map((t) => ({
      beloep: t.tildeltBeloep.beloep,
      valuta: t.tildeltBeloep.valuta,
      dato: t.tildelingsdato,
      ordning: t.stoetteordning?.navn || 'Ukjent ordning',
      giver: t.stoetteGiver?.navn || 'Ukjent giver',
    }))
    .sort((a, b) => new Date(b.dato).getTime() - new Date(a.dato).getTime());
}
